import { AsyncPipe, NgClass, NgTemplateOutlet, TitleCasePipe } from '@angular/common';
import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { Store } from '@ngrx/store';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';
import { ShortIdPipe, SplitPipe } from '@rocketfinancialcorp/rocket-ui/table';
import { DetailsItemIdComponent } from '@shared/components/details-item-id/details-item-id.component';
import { DetailsItemComponent } from '@shared/components/details-item/details-item.component';
import { TransactionHistoryComponent } from '@shared/components/transactions/transaction-history/transaction-history.component';
import { TemplateContextDirective } from '@shared/directives';
import { DropdownItem, TemplateContext, TransactionDetails, TransactionHistory } from '@shared/models';
import { DateFormatPipe, NotAvailablePipe, TeamMemberNamePipe } from '@shared/pipes';
import { AmountPipe } from '@shared/pipes/amount.pipe';
import { selectTransactionDetailsWithHistory, TransactionsActions } from '@store';
import { tap } from 'rxjs';

@Component({
  selector: 'app-transaction-details',
  imports: [
    AsyncPipe,
    DateFormatPipe,
    TeamMemberNamePipe,
    NotAvailablePipe,
    DetailsItemComponent,
    DetailsItemIdComponent,
    RktIconComponent,
    TitleCasePipe,
    ShortIdPipe,
    AmountPipe,
    SplitPipe,
    NgClass,
    TemplateContextDirective,
    NgTemplateOutlet,
    TransactionHistoryComponent,
  ],
  templateUrl: './transaction-details.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './transaction-details.component.scss',
})
export class TransactionDetailsComponent {
  store = inject(Store);

  transactionDetailsContext?: TemplateContext<TransactionDetails>;

  transactionHistoryContext?: TemplateContext<TransactionHistory[]>;

  isShowMetadata = false;

  transaction$ = this.store.select(selectTransactionDetailsWithHistory).pipe(
    tap({
      next: (transaction) => {
        this.selectedStatus = transaction?.transactionDetails?.latestStatus.status;
        this.setStatusOptions();
      },
    }),
  );

  selectedStatus?: string;

  statusOptions: DropdownItem[] = [];

  setSelectedStatus(item?: DropdownItem) {
    this.selectedStatus = item?.value;
  }

  setStatusOptions(): void {
    let options: DropdownItem[] = [];

    switch (this.selectedStatus) {
      case 'NEW':
        options = [
          { label: 'New', value: 'NEW' },
          { label: 'Pending', value: 'PENDING' },
          { label: 'Approved', value: 'APPROVED' },
          { label: 'Cancelled', value: 'CANCELLED' },
          { label: 'Declined', value: 'DECLINED' },
          { label: 'Error', value: 'ERROR' },
        ];
        break;

      case 'APPROVED':
        options = [
          { label: 'Approved', value: 'APPROVED' },
          { label: 'Settled', value: 'SETTLED' },
          { label: 'Cleared', value: 'CLEARED' },
          { label: 'Declined', value: 'DECLINED' },
          { label: 'Error', value: 'ERROR' },
          { label: 'Deposit Withheld', value: 'DEPOSIT_WITHHELD' },
        ];
        break;

      case 'PENDING':
        options = [
          { label: 'Pending', value: 'PENDING' },
          { label: 'Processing', value: 'PROCESSING' },
          { label: 'Approved', value: 'APPROVED' },
          { label: 'Cancelled', value: 'CANCELLED' },
          { label: 'Declined', value: 'DECLINED' },
          { label: 'Error', value: 'ERROR' },
        ];
        break;

      case 'PROCESSING':
        options = [
          { label: 'Processing', value: 'PROCESSING' },
          { label: 'Approved', value: 'APPROVED' },
          { label: 'Declined', value: 'DECLINED' },
          { label: 'Error', value: 'ERROR' },
        ];
        break;

      case 'DEPOSIT_WITHHELD':
        options = [
          { label: 'Deposit Withheld', value: 'DEPOSIT_WITHHELD' },
          { label: 'Cleared', value: 'CLEARED' },
        ];
        break;

      case 'ERROR':
        options = [
          { label: 'Error', value: 'ERROR' },
          { label: 'Processing', value: 'PROCESSING' },
          { label: 'Approved', value: 'APPROVED' },
          { label: 'Settled', value: 'SETTLED' },
          { label: 'Declined', value: 'DECLINED' },
        ];
        break;

      case 'DECLINED':
        options = [
          { label: 'Declined', value: 'DECLINED' },
          { label: 'Approved', value: 'APPROVED' },
          { label: 'Settled', value: 'SETTLED' },
        ];
        break;

      case 'SETTLED':
        options = [
          { label: 'Settled', value: 'SETTLED' },
          { label: 'Declined', value: 'DECLINED' },
        ];
        break;
      case 'CLEARED':
        options = [{ label: 'Cleared', value: 'CLEARED' }];
        break;

      default:
        break;
    }

    this.statusOptions = options;
  }

  updateTransactionStatus(transactionDetails: TransactionDetails) {
    if (transactionDetails.status === this.selectedStatus) return;

    this.store.dispatch(
      TransactionsActions.updateTransactionStatus({
        params: {
          businessAccountId: transactionDetails.businessAccountId,
          status: this.selectedStatus!,
          message: 'Manual status change',
        },
      }),
    );
  }
}
